import React, { useState, useRef, useEffect } from 'react'
import { Button, Modal, Form, message, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import SignatureCanvas from 'react-signature-canvas'
import { useDangerModel } from '../dangerModel'
import useGlobalModel from '@/model/globalModel'
import { getBaseImgLookUrl, getToken, oldGetBaseRequestUrl } from '@/utils/constants'
import { dangerService } from '../dangerService'
import { orderStatusEnum, orderTypeEnum } from '@/types/AppEnums'
import dayjs from 'dayjs'

const { Item } = Form

export const CheckAcceptance: React.FC = () => {
    const [signature, setSignature] = useState('')
    const sigCanvas = useRef<SignatureCanvas>(null)
    const { acceptModalVisible, setAcceptModalVisible, maintenanceId, loadData, dangerTab, acceptRecord } = useDangerModel()
    const {
        user: { createUser },
    } = useGlobalModel(({ user }) => [user])

    const isInspectionTab = dangerTab === 'inspection'

    //工单列表数据
    const [orderList, setOrderList] = useState<any[]>([])
    const [orderLoading, setOrderLoading] = useState(false)
    //选中的工单
    const [selectedOrderKeys, setSelectedOrderKeys] = useState<string[]>([])

    //弹窗打开时获取工单列表
    useEffect(() => {
        if (acceptModalVisible && acceptRecord) {
            fetchOrderList()
            setSelectedOrderKeys([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acceptModalVisible, acceptRecord])

    const fetchOrderList = async () => {
        setOrderLoading(true)
        try {
            const params = {
                customerVehicleCode: acceptRecord.vehicleNumber,
                startTime: dayjs(acceptRecord.createTime).format('YYYY-MM-DD'),
                endTime: dayjs().format('YYYY-MM-DD'),
            }
            const { retData } = await dangerService.hiddenOrderList(params)
            setOrderList(retData || [])
        } catch (error) {
            message.error('获取工单列表失败')
            setOrderList([])
        } finally {
            setOrderLoading(false)
        }
    }

    const handleOk = async () => {
        if (!signature) {
            message.warning('请签名！')
            return
        }
        const confirmData = {
            id: maintenanceId,
            signMd5: signature,
            signName: '电脑签名(验收)',
            createUser: createUser,
            orderCode: selectedOrderKeys.join(','),
        }
        if (isInspectionTab) {
            await dangerService.inspectionConfirm(confirmData)
        } else {
            await dangerService.adminFinish(confirmData)
        }
        setAcceptModalVisible(false)
        setSignature('')
        sigCanvas.current?.clear()
        setOrderList([])
        setSelectedOrderKeys([])
        await loadData()
    }

    const handleCancel = () => {
        setAcceptModalVisible(false)
        setSignature('')
        sigCanvas.current?.clear()
        setOrderList([])
        setSelectedOrderKeys([])
    }

    const dataURLToBlob = (dataURL: string) => {
        const arr: any = dataURL.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
        }
        return new Blob([u8arr], { type: mime })
    }
    const uploadSignature = async (val: any) => {
        const formData = new FormData()
        formData.append('appCode', 'assets')
        formData.append('fileMode', '0')
        formData.append('file', val, '电脑签名.png')

        try {
            const response = await fetch(`${oldGetBaseRequestUrl()}/fsrv/upload_file?Authorization=${getToken()}`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `${getToken()}`,
                },
            })

            if (!response.ok) {
                message.error('签名上传失败')
                return null
            }

            const data = await response.json()

            if (data && data.retData) {
                return data.retData
            } else {
                message.error('签名上传失败')
                return null
            }
        } catch (error) {
            console.error('Error uploading signature:', error)
            message.error('签名上传异常')
            return null
        }
    }
    const onSignatureChange = async () => {
        try {
            const dataURL = sigCanvas.current?.toDataURL() || ''
            if (dataURL) {
                const blob = dataURLToBlob(dataURL)
                const response = await uploadSignature(blob)
                if (response) {
                    setSignature(response)
                }
            }
        } catch (error) {
            console.error('Error uploading signature:', error)
        }
    }

    const clearSignature = () => {
        setSignature('')
        sigCanvas.current?.clear()
    }

    //工单列表列定义
    const orderColumns: ColumnsType<any> = [
        {
            title: '工单编号',
            width: 160,
            align: 'center',
            dataIndex: 'orderCode',
        },
        {
            title: '工单类型',
            width: 100,
            align: 'center',
            dataIndex: 'orderType',
            render: (text) => <span>{orderTypeEnum[text as keyof typeof orderTypeEnum] || text}</span>,
        },
        {
            title: '服务地点',
            width: 160,
            align: 'center',
            dataIndex: 'repairAddress',
        },
        {
            title: '登记日期',
            width: 140,
            align: 'center',
            dataIndex: 'createTime',
        },
        {
            title: '完工日期',
            width: 140,
            align: 'center',
            dataIndex: 'orderCompletedTime',
        },
        {
            title: '故障描述',
            width: 160,
            align: 'center',
            dataIndex: 'faultDesc',
        },
        {
            title: '维修团队',
            width: 160,
            align: 'center',
            dataIndex: 'teamName',
        },
        {
            title: '处理结果',
            width: 160,
            align: 'center',
            dataIndex: 'processResult',
        },
        {
            title: '总金额',
            width: 100,
            align: 'center',
            dataIndex: 'orderActualAmount',
        },
        {
            title: '审核人',
            width: 100,
            align: 'center',
            dataIndex: 'reviewerName',
        },
        {
            title: '工单状态',
            width: 100,
            align: 'center',
            dataIndex: 'orderStatus',
            render: (text) => <span>{orderStatusEnum[text as keyof typeof orderStatusEnum] || text}</span>,
        },
        {
            title: '备注',
            width: 160,
            align: 'center',
            dataIndex: 'remark',
        },
    ]

    return (
        <Modal title="验收并签字确认" open={acceptModalVisible} onOk={handleOk} onCancel={handleCancel} width={1300} destroyOnClose>
            <Form layout="vertical">
                <Item label="验收：">
                    <span>您已确认，是否工单已被维修技师维修完毕！</span>
                </Item>
                <Item label="关联工单（可多选）">
                    <Table
                        rowKey="orderCode"
                        loading={orderLoading}
                        columns={orderColumns}
                        dataSource={orderList}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 'max-content', y: 240 }}
                        rowSelection={{
                            selectedRowKeys: selectedOrderKeys,
                            onChange: (keys) => setSelectedOrderKeys(keys as string[]),
                        }}
                    />
                </Item>
                <Item label="签字确认">
                    <div style={{ width: '402px', height: '202px', border: '1px dashed' }}>
                        <SignatureCanvas
                            ref={sigCanvas}
                            penColor="black"
                            backgroundColor="white"
                            canvasProps={{ width: 400, height: 200 }}
                            onEnd={onSignatureChange}
                        />
                    </div>
                    <Button type="primary" style={{ marginTop: '10px' }} onClick={clearSignature}>
                        清除签字
                    </Button>
                </Item>
                {signature && (
                    <Item label="签字预览">
                        <img src={getBaseImgLookUrl(signature)} style={{ maxWidth: '50px', maxHeight: '70px' }} alt="签字预览" />
                    </Item>
                )}
            </Form>
        </Modal>
    )
}
